import { useState, useEffect, useCallback } from 'react'
import { rewardsAPI } from '../services/api'
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
  Button, Badge, Input, Label, Textarea,
  Tabs, TabsList, TabsTrigger, TabsContent,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '../components/ui'

const STATUS_COLORS = {
  PENDING: 'secondary',
  INPROGRESS: 'default',
  CLAIMABLE: 'default',
  REDEEMED: 'outline',
  EXPIRED: 'destructive',
}

function formatDate(d) {
  if (!d) return '—'
  try { return new Date(d).toLocaleDateString() } catch { return '—' }
}

function firstInProgressAt(statusHistory) {
  if (!Array.isArray(statusHistory)) return null
  const entries = statusHistory
    .filter(e => e?.status === 'INPROGRESS' && e?.changedAt)
    .map(e => new Date(e.changedAt))
    .filter(d => !Number.isNaN(d.getTime()))
  if (entries.length === 0) return null
  return new Date(Math.min(...entries.map(d => d.getTime())))
}

function formatMeta(r) {
  if (!r?.meta) return ''
  if (r.rewardType === 'CREDIT') return `$${((r.meta.amount || 0) / 100).toFixed(2)}`
  if (r.rewardType === 'DISCOUNT') {
    const val = r.meta.discountValue
    const unit = r.meta.discountType === 'percentage' ? '%' : '$'
    return `${r.meta.discountCode || ''} — ${unit === '%' ? val + '%' : '$' + val}`
  }
  return ''
}

export default function RewardsPage() {
  const [tab, setTab] = useState('catalog')

  // Catalog state
  const [catalog, setCatalog] = useState([])
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [catalogError, setCatalogError] = useState(null)
  const [catalogQuery, setCatalogQuery] = useState('')

  // Assignments state
  const [assignments, setAssignments] = useState([])
  const [assignmentsTotal, setAssignmentsTotal] = useState(0)
  const [assignmentsLoading, setAssignmentsLoading] = useState(false)
  const [assignmentsError, setAssignmentsError] = useState(null)
  const [filterRewardId, setFilterRewardId] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterEmail, setFilterEmail] = useState('')
  const [activity, setActivity] = useState({})
  const [activityLoading, setActivityLoading] = useState(false)

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false)

  // Assign dialog
  const [assignOpen, setAssignOpen] = useState(false)
  const [assignReward, setAssignReward] = useState(null)

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true)
    setCatalogError(null)
    try {
      const { data } = await rewardsAPI.listCatalog(catalogQuery ? { q: catalogQuery } : {})
      if (data.success) setCatalog(data.data.items)
      else throw new Error(data.error?.message || 'Failed to load catalog')
    } catch (err) {
      setCatalogError(err.response?.data?.error?.message || err.message)
    } finally {
      setCatalogLoading(false)
    }
  }, [catalogQuery])

  const loadAssignments = useCallback(async () => {
    setAssignmentsLoading(true)
    setAssignmentsError(null)
    try {
      const params = {}
      if (filterRewardId) params.rewardId = filterRewardId
      if (filterStatus) params.status = filterStatus
      if (filterEmail) params.email = filterEmail
      const { data } = await rewardsAPI.listAssignments(params)
      if (data.success) {
        setAssignments(data.data.items)
        setAssignmentsTotal(data.data.total)
        // Fire-and-forget activity lookup for unique segmentIds
        const segmentIds = [...new Set(
          data.data.items
            .map(a => a.customer?.segmentId)
            .filter(Boolean)
        )]
        if (segmentIds.length > 0) {
          setActivityLoading(true)
          setActivity({})
          rewardsAPI.getActivity(segmentIds)
            .then(({ data: actData }) => {
              if (actData.success) setActivity(actData.data.activity || {})
            })
            .catch((err) => {
              console.warn('[rewards] activity fetch failed', err)
            })
            .finally(() => setActivityLoading(false))
        } else {
          setActivity({})
        }
      } else throw new Error(data.error?.message || 'Failed to load assignments')
    } catch (err) {
      setAssignmentsError(err.response?.data?.error?.message || err.message)
    } finally {
      setAssignmentsLoading(false)
    }
  }, [filterRewardId, filterStatus, filterEmail])

  useEffect(() => { loadCatalog() }, [loadCatalog])
  useEffect(() => { if (tab === 'assignments') loadAssignments() }, [tab, loadAssignments])

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Rewards</h1>
          <p className="text-muted-foreground mt-1">
            Manage MDExam rewards catalog and customer assignments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setCreateOpen(true)}>Create reward</Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="catalog">Catalog</TabsTrigger>
          <TabsTrigger value="assignments">Assignments</TabsTrigger>
        </TabsList>

        <TabsContent value="catalog" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <CardTitle>Reward Catalog</CardTitle>
                  <CardDescription>All reward definitions in mdexam_legacy</CardDescription>
                </div>
                <Input
                  placeholder="Search title, description, key..."
                  value={catalogQuery}
                  onChange={(e) => setCatalogQuery(e.target.value)}
                  className="max-w-sm"
                />
              </div>
            </CardHeader>
            <CardContent>
              {catalogError && <p className="text-destructive text-sm mb-3">{catalogError}</p>}
              {catalogLoading ? (
                <p className="text-muted-foreground text-sm">Loading...</p>
              ) : catalog.length === 0 ? (
                <p className="text-muted-foreground text-sm">No rewards found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-medium">Title</th>
                        <th className="text-left py-2 px-3 font-medium">Key</th>
                        <th className="text-left py-2 px-3 font-medium">Type</th>
                        <th className="text-left py-2 px-3 font-medium">Category</th>
                        <th className="text-left py-2 px-3 font-medium">Value</th>
                        <th className="text-right py-2 px-3 font-medium">Assigned</th>
                        <th className="text-right py-2 px-3 font-medium">Active (30d)</th>
                        <th className="text-right py-2 px-3 font-medium">Redeemed</th>
                        <th className="text-right py-2 px-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {catalog.map((r) => {
                        const total = r.assignment_counts?.total || 0
                        const active = r.assignment_counts?.active_30d
                        const pct = total > 0 && typeof active === 'number' ? Math.round((active / total) * 100) : null
                        return (
                          <tr key={r._id} className="border-b hover:bg-muted/50">
                            <td className="py-2 px-3 font-medium">{r.title}</td>
                            <td className="py-2 px-3 font-mono text-xs">{r.rewardKey}</td>
                            <td className="py-2 px-3"><Badge variant="outline">{r.rewardType}</Badge></td>
                            <td className="py-2 px-3"><Badge variant="secondary">{r.rewardCategory}</Badge></td>
                            <td className="py-2 px-3 text-xs">{formatMeta(r)}</td>
                            <td className="py-2 px-3 text-right font-mono">{total}</td>
                            <td className="py-2 px-3 text-right font-mono text-xs">
                              {active === null || active === undefined ? (
                                <span className="text-muted-foreground">—</span>
                              ) : (
                                <span>{active}{pct !== null && total > 0 ? ` (${pct}%)` : ''}</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-right font-mono">{r.assignment_counts?.by_status?.REDEEMED || 0}</td>
                            <td className="py-2 px-3 text-right">
                              <Button
                                variant="outline" size="sm"
                                onClick={() => { setAssignReward(r); setAssignOpen(true) }}
                              >
                                Assign
                              </Button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assignments" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Assignments</CardTitle>
              <CardDescription>Who has what — filter by reward, status, or email</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3 mb-4">
                <Select value={filterRewardId || 'all'} onValueChange={(v) => setFilterRewardId(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-64"><SelectValue placeholder="All rewards" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All rewards</SelectItem>
                    {catalog.map(r => (
                      <SelectItem key={r._id} value={r._id}>{r.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-40"><SelectValue placeholder="All statuses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="PENDING">PENDING</SelectItem>
                    <SelectItem value="INPROGRESS">INPROGRESS</SelectItem>
                    <SelectItem value="CLAIMABLE">CLAIMABLE</SelectItem>
                    <SelectItem value="REDEEMED">REDEEMED</SelectItem>
                    <SelectItem value="EXPIRED">EXPIRED</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Email contains..."
                  value={filterEmail}
                  onChange={(e) => setFilterEmail(e.target.value)}
                  className="max-w-xs"
                />
                <Button variant="outline" onClick={loadAssignments} disabled={assignmentsLoading}>
                  {assignmentsLoading ? 'Loading...' : 'Apply'}
                </Button>
              </div>

              {assignmentsError && <p className="text-destructive text-sm mb-3">{assignmentsError}</p>}

              {assignmentsLoading ? (
                <p className="text-muted-foreground text-sm">Loading...</p>
              ) : assignments.length === 0 ? (
                <p className="text-muted-foreground text-sm">No assignments match these filters.</p>
              ) : (
                <>
                  {(() => {
                    const assigneesWithSegmentId = assignments.filter(a => a.customer?.segmentId)
                    const activeNow = assigneesWithSegmentId.filter(a => activity[a.customer.segmentId]?.is_active).length
                    return (
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-muted-foreground">
                          Showing {assignments.length} of {assignmentsTotal.toLocaleString()}
                          {assigneesWithSegmentId.length > 0 && !activityLoading && (
                            <>
                              {' · '}
                              <span className="font-medium text-foreground">
                                {activeNow} of {assigneesWithSegmentId.length} active
                              </span>
                              {' (last 30d)'}
                            </>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Active = any portal page view in last 30 days
                          {activityLoading && ' · loading activity...'}
                        </p>
                      </div>
                    )
                  })()}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 font-medium">Customer</th>
                          <th className="text-left py-2 px-3 font-medium">Reward</th>
                          <th className="text-left py-2 px-3 font-medium">Status</th>
                          <th className="text-left py-2 px-3 font-medium">Milestone</th>
                          <th className="text-left py-2 px-3 font-medium">Active (30d)</th>
                          <th className="text-right py-2 px-3 font-medium">Views (30d)</th>
                          <th className="text-left py-2 px-3 font-medium">Last seen</th>
                          <th className="text-left py-2 px-3 font-medium">Expires</th>
                          <th className="text-left py-2 px-3 font-medium">Assigned</th>
                          <th className="text-left py-2 px-3 font-medium">In Progress At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignments.map((a) => {
                          const act = a.customer?.segmentId ? activity[a.customer.segmentId] : null
                          const hasSegmentId = Boolean(a.customer?.segmentId)
                          return (
                            <tr key={a._id} className="border-b hover:bg-muted/50">
                              <td className="py-2 px-3">{a.customer?.email || <span className="text-muted-foreground">(no email)</span>}</td>
                              <td className="py-2 px-3">{a.reward?.title || <span className="text-muted-foreground">(deleted)</span>}</td>
                              <td className="py-2 px-3">
                                <Badge variant={STATUS_COLORS[a.status] || 'outline'}>{a.status}</Badge>
                              </td>
                              <td className="py-2 px-3 font-mono text-xs">{a.currentMilestone ?? '—'}</td>
                              <td className="py-2 px-3">
                                {!hasSegmentId ? (
                                  <span className="text-muted-foreground text-xs">—</span>
                                ) : activityLoading && !act ? (
                                  <span className="text-muted-foreground text-xs">…</span>
                                ) : act?.is_active ? (
                                  <Badge variant="default" className="bg-green-600 hover:bg-green-600">Active</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>
                                )}
                              </td>
                              <td className="py-2 px-3 text-right font-mono text-xs">
                                {hasSegmentId ? (act?.page_views_30d ?? 0) : '—'}
                              </td>
                              <td className="py-2 px-3 text-xs">
                                {hasSegmentId ? formatDate(act?.last_seen) : '—'}
                              </td>
                              <td className="py-2 px-3 text-xs">{formatDate(a.expiresAt)}</td>
                              <td className="py-2 px-3 text-xs">{formatDate(a.createdAt)}</td>
                              <td className="py-2 px-3 text-xs">{formatDate(firstInProgressAt(a.statusHistory))}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <CreateRewardDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => { setCreateOpen(false); loadCatalog() }}
      />

      <AssignDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        reward={assignReward}
        onAssigned={() => { setAssignOpen(false); loadCatalog(); if (tab === 'assignments') loadAssignments() }}
      />
    </div>
  )
}

function CreateRewardDialog({ open, onOpenChange, onCreated }) {
  const empty = {
    title: '', description: '', rewardType: 'CREDIT', rewardCategory: 'Mission',
    rewardKey: '', metaAmountDollars: '', discountCode: '', discountType: 'percentage', discountValue: '',
    milestoneCount: '', milestoneLabel: '',
    imageUrl: '', markdown: '', externalLink: '',
    ctaText: '', successHeading: '', successMessage: '',
  }
  const [form, setForm] = useState(empty)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)

  useEffect(() => {
    if (open) { setForm(empty); setError(null); setResult(null) }
  }, [open])

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  const submit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const body = {
        title: form.title,
        description: form.description,
        rewardType: form.rewardType,
        rewardCategory: form.rewardCategory,
        rewardKey: form.rewardKey,
        meta: {},
        imageUrl: form.imageUrl || undefined,
        markdown: form.markdown || undefined,
        externalLink: form.externalLink || undefined,
        ctaText: form.ctaText || undefined,
        successHeading: form.successHeading || undefined,
        successMessage: form.successMessage || undefined,
      }
      if (form.rewardType === 'CREDIT') {
        const dollars = parseFloat(form.metaAmountDollars)
        if (!(dollars > 0)) throw new Error('Enter a credit amount in dollars')
        body.meta = { amount: Math.round(dollars * 100) }
      } else if (form.rewardType === 'DISCOUNT') {
        if (!form.discountCode || !form.discountValue) throw new Error('Discount code and value required')
        body.meta = {
          discountCode: form.discountCode,
          discountType: form.discountType,
          discountValue: parseFloat(form.discountValue),
        }
      }
      if (form.rewardCategory === 'Milestone') {
        const count = parseInt(form.milestoneCount, 10)
        if (!(count > 0)) throw new Error('Milestone target count required')
        body.milestoneConfig = {
          milestone: count,
          milestonKey: form.rewardKey,
          milestoneLabel: form.milestoneLabel || form.title,
        }
      }
      const { data } = await rewardsAPI.createReward(body)
      if (!data.success) throw new Error(data.error?.message || 'Create failed')
      setResult(data.data)
      onCreated()
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create reward</DialogTitle>
          <DialogDescription>New reward definition in mdexam_legacy</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2">
            <Label>Title *</Label>
            <Input value={form.title} onChange={(e) => update('title', e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label>Description *</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => update('description', e.target.value)} />
          </div>

          <div>
            <Label>Reward Type *</Label>
            <Select value={form.rewardType} onValueChange={(v) => update('rewardType', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="CREDIT">CREDIT</SelectItem>
                <SelectItem value="DISCOUNT">DISCOUNT</SelectItem>
                <SelectItem value="FREE">FREE</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Category *</Label>
            <Select value={form.rewardCategory} onValueChange={(v) => update('rewardCategory', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Mission">Mission</SelectItem>
                <SelectItem value="Milestone">Milestone</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2">
            <Label>Reward Key *</Label>
            <Input
              value={form.rewardKey}
              onChange={(e) => update('rewardKey', e.target.value)}
              placeholder="e.g. google-review"
            />
          </div>

          {form.rewardType === 'CREDIT' && (
            <div className="col-span-2">
              <Label>Credit Amount (USD) *</Label>
              <Input
                type="number" step="0.01" min="0"
                value={form.metaAmountDollars}
                onChange={(e) => update('metaAmountDollars', e.target.value)}
                placeholder="25.00"
              />
            </div>
          )}

          {form.rewardType === 'DISCOUNT' && (
            <>
              <div>
                <Label>Discount Code *</Label>
                <Input value={form.discountCode} onChange={(e) => update('discountCode', e.target.value)} placeholder="SAVE20" />
              </div>
              <div>
                <Label>Discount Type *</Label>
                <Select value={form.discountType} onValueChange={(v) => update('discountType', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="amount">Fixed Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Discount Value *</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.discountValue}
                  onChange={(e) => update('discountValue', e.target.value)}
                />
              </div>
            </>
          )}

          {form.rewardCategory === 'Milestone' && (
            <>
              <div>
                <Label>Milestone Target Count *</Label>
                <Input
                  type="number" min="1"
                  value={form.milestoneCount}
                  onChange={(e) => update('milestoneCount', e.target.value)}
                  placeholder="3"
                />
              </div>
              <div>
                <Label>Milestone Label</Label>
                <Input
                  value={form.milestoneLabel}
                  onChange={(e) => update('milestoneLabel', e.target.value)}
                  placeholder="Complete 3 check-ins"
                />
              </div>
            </>
          )}

          <div className="col-span-2">
            <Label>External Link</Label>
            <Input value={form.externalLink} onChange={(e) => update('externalLink', e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label>CTA Text</Label>
            <Input value={form.ctaText} onChange={(e) => update('ctaText', e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label>Success Heading</Label>
            <Input value={form.successHeading} onChange={(e) => update('successHeading', e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label>Success Message</Label>
            <Textarea rows={2} value={form.successMessage} onChange={(e) => update('successMessage', e.target.value)} />
          </div>
        </div>

        {error && <p className="text-destructive text-sm">{error}</p>}
        {result && <p className="text-green-600 text-sm">Created: {result._id}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? 'Creating...' : 'Create reward'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function AssignDialog({ open, onOpenChange, reward, onAssigned }) {
  const [mode, setMode] = useState('emails') // emails | all_active | random_active
  const [emails, setEmails] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [randomCount, setRandomCount] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)

  const [poolLoading, setPoolLoading] = useState(false)
  const [pool, setPool] = useState(null) // {matched_customers, total_active_segment_ids, preview_emails}

  useEffect(() => {
    if (open) {
      setMode('emails')
      setEmails('')
      setExpiresAt('')
      setRandomCount('')
      setError(null)
      setResult(null)
      setPool(null)
    }
  }, [open])

  // Fetch pool when user switches to an active mode
  useEffect(() => {
    if (!open || mode === 'emails') return
    if (pool) return
    setPoolLoading(true)
    rewardsAPI.getActivePool({ preview: 10 })
      .then(({ data }) => {
        if (data.success) setPool(data.data)
        else throw new Error(data.error?.message || 'Pool lookup failed')
      })
      .catch((err) => setError(err.response?.data?.error?.message || err.message))
      .finally(() => setPoolLoading(false))
  }, [open, mode, pool])

  const submit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      let data
      if (mode === 'emails') {
        const list = emails
          .split(/[\s,;\n]+/)
          .map(e => e.trim())
          .filter(Boolean)
        if (list.length === 0) throw new Error('Enter at least one email')
        const body = { rewardId: reward._id, emails: list }
        if (expiresAt) body.expiresAt = new Date(expiresAt).toISOString()
        ;({ data } = await rewardsAPI.assign(body))
      } else {
        const body = {
          rewardId: reward._id,
          mode: mode === 'all_active' ? 'all' : 'random',
        }
        if (mode === 'random_active') {
          const n = parseInt(randomCount, 10)
          if (!(n > 0)) throw new Error('Enter a positive count')
          body.count = n
        }
        if (expiresAt) body.expiresAt = new Date(expiresAt).toISOString()
        ;({ data } = await rewardsAPI.assignActive(body))
      }
      if (!data.success) throw new Error(data.error?.message || 'Assign failed')
      setResult({ ...data.data, _mode: mode })
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const poolSize = pool?.matched_customers ?? 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Assign reward</DialogTitle>
          <DialogDescription>
            {reward ? `Assign "${reward.title}"` : 'No reward selected'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label>Recipients</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="emails">Specific emails</SelectItem>
                <SelectItem value="all_active">All active customers (30d)</SelectItem>
                <SelectItem value="random_active">Random sample of active customers (30d)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode === 'emails' && (
            <div>
              <Label>Customer emails</Label>
              <Textarea
                rows={6}
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                placeholder="One per line, or comma/space-separated"
                className="font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Emails not found in customer_enhanced will be reported back. Duplicate PENDING assignments are skipped.
              </p>
            </div>
          )}

          {mode !== 'emails' && (
            <div className="border rounded-md p-3 bg-muted/40 text-sm space-y-1">
              {poolLoading ? (
                <p className="text-muted-foreground">Looking up active pool…</p>
              ) : pool ? (
                <>
                  <p>
                    <span className="font-medium">{poolSize.toLocaleString()}</span> customers active in last 30 days
                    {pool.total_active_segment_ids > poolSize && (
                      <span className="text-muted-foreground text-xs">
                        {' '}(of {pool.total_active_segment_ids.toLocaleString()} tracked ids)
                      </span>
                    )}
                  </p>
                  {pool.preview_emails?.length > 0 && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-muted-foreground">Preview emails</summary>
                      <pre className="mt-2 whitespace-pre-wrap font-mono">{pool.preview_emails.join('\n')}</pre>
                    </details>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground">Pool not loaded</p>
              )}
            </div>
          )}

          {mode === 'random_active' && (
            <div>
              <Label>Sample size</Label>
              <Input
                type="number" min="1"
                value={randomCount}
                onChange={(e) => setRandomCount(e.target.value)}
                placeholder={pool ? `1–${poolSize}` : 'e.g. 50'}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Random customers picked uniformly from the active pool above.
              </p>
            </div>
          )}

          <div>
            <Label>Expires at (optional)</Label>
            <Input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
        </div>

        {error && <p className="text-destructive text-sm">{error}</p>}
        {result && (
          <div className="text-sm space-y-1 border rounded-md p-3 bg-muted/40">
            <p><span className="font-medium">Assigned:</span> {result.assigned}</p>
            <p><span className="font-medium">Skipped (existing PENDING):</span> {result.skipped_existing}</p>
            {result._mode === 'emails' ? (
              <>
                <p><span className="font-medium">Not found:</span> {result.not_found_emails?.length || 0}</p>
                {result.not_found_emails?.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground">Show not-found emails</summary>
                    <pre className="mt-2 whitespace-pre-wrap font-mono">{result.not_found_emails.join('\n')}</pre>
                  </details>
                )}
              </>
            ) : (
              <>
                <p><span className="font-medium">Pool size:</span> {result.pool_size?.toLocaleString()}</p>
                <p><span className="font-medium">Selected:</span> {result.selected_size?.toLocaleString()}</p>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); if (result) onAssigned() }} disabled={submitting}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button onClick={submit} disabled={submitting || !reward || (mode !== 'emails' && poolLoading)}>
              {submitting ? 'Assigning...' : 'Assign'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
